import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Brain, Users, Lock, CheckCircle, ArrowRight, ArrowDown, Zap, Eye, RotateCcw,
  FileText, Layers, Activity, Globe, Server, Code, Database, Clock,
  Target, Sparkles, Network, Cpu, GitBranch, AlertTriangle, Play, Pause
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';
import CAOArchitectureDiagram from '../components/CAOArchitectureDiagram';

export default function ArchitecturePage() {
  const [activeLayer, setActiveLayer] = useState(null);

  const architectureLayers = [
    {
      id: 'inputs',
      name: 'Input Channels',
      description: 'All requests enter through normalized input channels',
      icon: Network,
      color: 'slate',
      details: ['REST API', 'GraphQL', 'SDK calls', 'Webhooks', 'CLI commands'],
    },
    {
      id: 'orchestrator',
      name: 'Executive Orchestrator',
      description: 'Routes requests, detects conflicts, ensures vetoes are respected',
      icon: Cpu,
      color: 'blue',
      details: ['Intake normalization', 'Classification', 'Routing to council', 'Conflict detection', 'Veto enforcement'],
    },
    {
      id: 'council',
      name: 'Executive Agent Council',
      description: 'Distributed deliberation across specialized AI executives',
      icon: Users,
      color: 'purple',
      details: ['CEO: Strategy & Risk', 'CPO: Product Intent', 'CTO: Architecture & Security', 'CAO: Behavior & Trust'],
    },
    {
      id: 'gate',
      name: 'CAO Behavioral Gate',
      description: 'Final authority on autonomy, safety, and behavioral constraints',
      icon: Shield,
      color: 'amber',
      details: ['Approve/deny autonomy tiers', 'Pause or rollback agents', 'Block irreversible paths', 'Log all overrides'],
    },
    {
      id: 'policy',
      name: 'Policy Engine',
      description: 'Translates executive decisions into enforceable system rules',
      icon: FileText,
      color: 'cyan',
      details: ['ABAC enforcement', 'Guardrails', 'Autonomy tier validation', 'Approval gates', 'Tool constraints'],
    },
    {
      id: 'gateway',
      name: 'Tool Gateway',
      description: 'Single execution choke point for all external operations',
      icon: Zap,
      color: 'green',
      details: ['Short-lived credentials', 'Strict allowlists', 'Spend limits', 'Scope limits', 'Tool permissions'],
    },
    {
      id: 'audit',
      name: 'Audit & Telemetry',
      description: 'Immutable logging for compliance and observability',
      icon: Activity,
      color: 'rose',
      details: ['Immutable logs', 'SOC-2 ready', 'ISO 27001 ready', 'GDPR ready', 'HIPAA ready'],
    },
  ];

  const executiveCouncil = [
    {
      role: 'CEO',
      title: 'Strategy & Risk',
      question: 'Should we do this?',
      veto: 'Strategic Deadlock',
      icon: Target,
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      role: 'CPO',
      title: 'Product Intent',
      question: 'Should this exist?',
      veto: 'User Harm',
      icon: Sparkles,
      gradient: 'from-purple-500 to-violet-600',
    },
    {
      role: 'CTO',
      title: 'Architecture & Security',
      question: 'Can this be built safely?',
      veto: 'Security / Reliability',
      icon: Code,
      gradient: 'from-green-500 to-emerald-600',
    },
    {
      role: 'CAO',
      title: 'Behavior, Trust, Autonomy',
      question: 'How should this behave?',
      veto: 'Autonomy / Safety',
      icon: Shield,
      gradient: 'from-amber-500 to-orange-600',
    },
  ];

  const coreConstraints = [
    {
      icon: AlertTriangle,
      title: 'Cannot Self-Escalate',
      description: 'No agent can increase its own autonomy level',
      color: 'amber',
    },
    {
      icon: Lock,
      title: 'Cannot Remove Safeguards',
      description: 'Safety constraints are immutable at runtime',
      color: 'rose',
    },
    {
      icon: FileText,
      title: 'Must Log Override Rationale',
      description: 'Every exception is documented and auditable',
      color: 'blue',
    },
    {
      icon: Pause,
      title: 'No Direct Tool Execution',
      description: 'All execution flows through the Tool Gateway',
      color: 'green',
    },
  ];

  return (
    <div className="relative min-h-screen pt-20">
      <AnimatedBackground variant="cyber" />
      <ParticleBackground density={30} />

      {/* Hero Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
              <Layers className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400 font-medium">Enterprise Architecture</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="text-white">System</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400">
                Architecture
              </span>
            </h1>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto mb-8">
              The CAO Platform is an executive-governed delegation system where autonomy is earned,
              constrained, audited, and never self-authorized.
            </p>

            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-slate-300">SOC-2 Ready</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-slate-300">ISO 27001 Ready</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-slate-300">GDPR Compliant</span>
              </div>
            </div>
          </div>

          {/* Interactive Architecture Diagram */}
          <div className="mb-16">
            <CAOArchitectureDiagram />
          </div>
        </div>
      </section>

      {/* Canonical Flow Section */}
      <section className="relative py-24 bg-slate-900/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Canonical</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Flow
              </span>
            </h2>
            <p className="text-xl text-slate-400">
              Every request follows this path — no exceptions, no shortcuts.
            </p>
          </div>

          <div className="space-y-4">
            {architectureLayers.map((layer, i) => (
              <div key={layer.id}>
                <GlowingCard
                  glowColor={layer.color}
                  className={`cursor-pointer transition-all duration-300 ${activeLayer === layer.id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setActiveLayer(activeLayer === layer.id ? null : layer.id)}
                >
                  <div className="p-6">
                    <div className="flex items-center gap-6">
                      <div className={`w-14 h-14 rounded-xl bg-${layer.color}-500/10 border border-${layer.color}-500/20 flex items-center justify-center shrink-0`}>
                        <layer.icon className={`w-7 h-7 text-${layer.color}-400`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">{layer.name}</h3>
                        <p className="text-slate-400">{layer.description}</p>
                      </div>
                      <ArrowRight className={`w-5 h-5 text-slate-500 transition-transform ${activeLayer === layer.id ? 'rotate-90' : ''}`} />
                    </div>

                    {activeLayer === layer.id && (
                      <div className="mt-6 pt-6 border-t border-slate-700">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                          {layer.details.map((detail, j) => (
                            <div key={j} className="flex items-center gap-2 text-sm text-slate-400">
                              <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                              {detail}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </GlowingCard>

                {i < architectureLayers.length - 1 && (
                  <div className="flex justify-center py-2">
                    <ArrowDown className="w-6 h-6 text-slate-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Executive Council Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Executive Agent</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                Council
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Distributed authority across specialized AI executives. No single agent can
              unilaterally define purpose, deploy autonomy, or govern behavior.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {executiveCouncil.map((agent, i) => (
              <GlowingCard key={i} glowColor={agent.role === 'CAO' ? 'amber' : agent.role === 'CEO' ? 'blue' : agent.role === 'CPO' ? 'purple' : 'green'}>
                <div className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${agent.gradient} flex items-center justify-center mb-6 shadow-lg`}>
                    <agent.icon className="w-7 h-7 text-white" />
                  </div>

                  <div className="text-2xl font-bold text-white mb-1">{agent.role}</div>
                  <div className="text-sm text-slate-400 mb-4">{agent.title}</div>

                  <p className="text-lg text-slate-300 italic mb-4">"{agent.question}"</p>

                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span className="text-slate-400">Veto: <span className="text-white">{agent.veto}</span></span>
                  </div>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* CAO Behavioral Gate Section */}
      <section className="relative py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                <span className="text-white">CAO Behavioral Gate</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                  Final Authority
                </span>
              </h2>

              <p className="text-xl text-slate-400 mb-8">
                The hard governance boundary of the system. Represented by the CAO platform role —
                the ultimate arbiter of autonomy, safety, and behavioral constraints.
              </p>

              <div className="space-y-4">
                <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Approve / Deny Autonomy Tiers
                  </h4>
                  <p className="text-slate-400 text-sm">Control which autonomy level an agent can operate at</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <Pause className="w-5 h-5 text-amber-400" />
                    Pause or Rollback Agents
                  </h4>
                  <p className="text-slate-400 text-sm">Instantly halt operations and reverse actions</p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-rose-400" />
                    Block Irreversible Execution Paths
                  </h4>
                  <p className="text-slate-400 text-sm">Prevent actions that cannot be undone</p>
                </div>
              </div>
            </div>

            <div>
              <GlowingCard glowColor="amber">
                <div className="p-8">
                  <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                    <Shield className="w-6 h-6 text-amber-400" />
                    Core Constraints
                  </h3>
                  <div className="space-y-4">
                    {coreConstraints.map((constraint, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-lg">
                        <div className={`w-10 h-10 rounded-lg bg-${constraint.color}-500/10 border border-${constraint.color}-500/20 flex items-center justify-center shrink-0`}>
                          <constraint.icon className={`w-5 h-5 text-${constraint.color}-400`} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white mb-1">{constraint.title}</h4>
                          <p className="text-sm text-slate-400">{constraint.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </GlowingCard>
            </div>
          </div>
        </div>
      </section>

      {/* Key Principles Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Architectural</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-400">
                Principles
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <GlowingCard glowColor="green">
              <div className="p-6">
                <div className="text-4xl mb-4">1</div>
                <h3 className="text-xl font-bold text-white mb-2">No Single Agent Authority</h3>
                <p className="text-slate-400">
                  No single agent may unilaterally define purpose, deploy autonomy, or govern behavior.
                  All high-impact decisions require council deliberation.
                </p>
              </div>
            </GlowingCard>

            <GlowingCard glowColor="blue">
              <div className="p-6">
                <div className="text-4xl mb-4">2</div>
                <h3 className="text-xl font-bold text-white mb-2">Deliberation Before Execution</h3>
                <p className="text-slate-400">
                  All high-impact actions pass through the Executive Orchestrator and Council
                  before any execution occurs. No shortcuts.
                </p>
              </div>
            </GlowingCard>

            <GlowingCard glowColor="purple">
              <div className="p-6">
                <div className="text-4xl mb-4">3</div>
                <h3 className="text-xl font-bold text-white mb-2">Single Execution Choke Point</h3>
                <p className="text-slate-400">
                  No agent executes tools directly. Ever. All external operations flow through
                  the Tool Gateway with strict validation.
                </p>
              </div>
            </GlowingCard>

            <GlowingCard glowColor="amber">
              <div className="p-6">
                <div className="text-4xl mb-4">4</div>
                <h3 className="text-xl font-bold text-white mb-2">Always-On Audit</h3>
                <p className="text-slate-400">
                  Every decision, override, tool execution, and autonomy change is logged
                  to an immutable audit trail. Complete observability.
                </p>
              </div>
            </GlowingCard>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-6">
          <GlowingCard glowColor="blue" className="overflow-hidden">
            <div className="relative p-12 text-center">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-cyan-600/20" />

              <div className="relative">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                  Explore the Full Documentation
                </h2>
                <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
                  Dive deeper into the technical architecture, API reference, and integration guides.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/docs"
                    className="group relative px-8 py-4 rounded-xl font-semibold overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500" />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="relative text-white flex items-center justify-center gap-2">
                      <FileText className="w-5 h-5" />
                      View Documentation
                    </span>
                  </Link>

                  <Link
                    to="/demo"
                    className="px-8 py-4 rounded-xl font-semibold border border-slate-600 hover:border-blue-500/50 bg-slate-900/50 hover:bg-blue-500/10 text-slate-300 hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                    <Play className="w-5 h-5" />
                    Watch Demo
                  </Link>
                </div>
              </div>
            </div>
          </GlowingCard>
        </div>
      </section>
    </div>
  );
}
